import {
  AddCircleOutline,
  AddRounded,
  GradingOutlined,
  HistoryOutlined,
} from "@mui/icons-material";
import CalculateOutlined from "@mui/icons-material/CalculateOutlined";
import ChecklistOutlined from "@mui/icons-material/ChecklistOutlined";
import CodeOutlined from "@mui/icons-material/CodeOutlined";
import NoteAltOutlined from "@mui/icons-material/NoteAltOutlined";
import QuestionAnswerOutlined from "@mui/icons-material/QuestionAnswerOutlined";
import { Button, useTheme } from "@mui/material";
import Badge from "@mui/material/Badge";
import Box from "@mui/material/Box";
import { blue } from "@mui/material/colors";
import Tooltip from "@mui/material/Tooltip";
import size from "lodash/size";
import PropTypes from "prop-types";
import { Fragment, lazy, memo, Suspense, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import Flx from "../../components/layout/Flx";
import LoadingFallback from "../../components/LoadingFallback";
import { loanDrilldownSidebarTypes } from "../../constants/enums/loanDrilldownScreenSidebarOptions";
import { useEnv } from "../../hooks/useEnv";
import { usePermissionsHook } from "../../hooks/usePermissionsHook";
import {
  sidebarClear,
  sidebarTypeToggle,
} from "../../redux/actions/sidebarActions";
import DrilldownSidebarPane from "./sidebar/DrilldownSidebarPane";
import DrilldownSidebarContentRenderer from "./sidebar/DrilldownSidebarContentRenderer";
import { useReduxHook } from "../../hooks/useReduxHook";
import { useNavigate } from "react-router-dom";

const LoanDrilldownScreenSidebar = memo(() => {
  const dispatch = useDispatch();
  const sidebarType = useSelector((state) => state.sidebar?.type);

  useEffect(() => {
    // Clear the side drawer state on unmount
    return () => {
      dispatch(sidebarClear());
    };
  }, [dispatch]);

  if (!sidebarType) {
    return (
      <CollapsedContentWrapper>
        <CollapsedContentButtons />
      </CollapsedContentWrapper>
    );
  }

  return (
    <>
      <Suspense
        fallback={
          <DrilldownSidebarPane
            // title="File Preview"
            // onClose={handleHideComments}
            bodyPadding={0}
            initialWidth="580px"
            bodyOverflow="hidden"
          >
            <LoadingFallback
              sx={{
                flexGrow: 1,
                // background: "red",
                background: "#ffffff",
                width: "100%",
                height: "100%",
              }}
            />
          </DrilldownSidebarPane>
        }
      >
        <DrilldownSidebarContentRenderer type={sidebarType} />
      </Suspense>
      <CollapsedContentButtons />
    </>
  );
});

/** Sidebar logic to route between different sidebar types */

const CollapsedContentWrapper = ({ children }) => {
  const styles = useMemo(() => {
    return {
      borderLeft: "1px solid #e0e0e0",
      height: "100%",
      display: "flex",
      background: "#ffffff",
      flexDirection: "column",
      gap: 1,
      flexShrink: 0,
      overflowY: "auto",
      ".MuiSvgIcon-root": { fontSize: 24 },
    };
  }, []);

  return (
    <Box sx={styles}>
      <Flx column>{children}</Flx>
    </Box>
  );
};

const CollapsedContentButtons = () => {
  const loanDrilldown = useSelector((state) => state.loanDrilldown);
  const { isUnderwriter } = usePermissionsHook();
  const { isHighLevelUser } = useReduxHook();
  const { isResfin } = useEnv();

  const showUWButtons = isUnderwriter || isResfin || isHighLevelUser;
  return (
    <CollapsedContentWrapper>
      <CollapsedSiderbarButton
        type={loanDrilldownSidebarTypes.CREATE_NEW}
        tooltip="Create a new item on this Loan"
        // label="Loan Comments"
        label="Create/Add"
      >
        <AddCircleOutline className="thin-8" />
      </CollapsedSiderbarButton>
      <CollapsedSiderbarButton
        type={loanDrilldownSidebarTypes.COMMENTS}
        tooltip="View Comments"
        // label="Loan Comments"
        label="Comments"
      >
        <Badge badgeContent={size(loanDrilldown?.comments)} color="primary">
          <QuestionAnswerOutlined className="thin-8" />
        </Badge>
      </CollapsedSiderbarButton>
      {showUWButtons && (
        <Fragment>
          <CollapsedSiderbarButton
            type={loanDrilldownSidebarTypes.UW_NOTES}
            tooltip="View Underwriting Notes"
            // label="Underwriting Notepad"
            label="UW Notepad"
          >
            <NoteAltOutlined className="thin-8" />
          </CollapsedSiderbarButton>
          {!isResfin && (
            <CollapsedSiderbarButton
              type={loanDrilldownSidebarTypes.UW_CHECKLIST}
              tooltip="View Underwriting Checklist"
              // label="Underwriting Checklist"
              label="UW Checklist"
            >
              <ChecklistOutlined className="thin-8" />
            </CollapsedSiderbarButton>
          )}
        </Fragment>
      )}
      <CollapsedSiderbarButton
        type={loanDrilldownSidebarTypes.UW_STIPS_AND_CONDITIONS}
        tooltip="View Underwriting Stips & Conditions"
        // label="Underwriting Checklist"
        label="UW Stips & Conditions"
      >
        <GradingOutlined className="thin-8" />
      </CollapsedSiderbarButton>
      <CollapsedSiderbarButton
        type={loanDrilldownSidebarTypes.LOAN_TOTALS}
        tooltip="View Loan Totals"
        label="Loan Totals"
      >
        <CalculateOutlined className="thin-8" />
      </CollapsedSiderbarButton>
      <CollapsedSiderbarButton
        type={loanDrilldownSidebarTypes.LOAN_CHANGE_HISTORY}
        tooltip="View Loan Changes History"
        label="Change History"
      >
        <HistoryOutlined className="thin-8" />
      </CollapsedSiderbarButton>
      <CollapsedSiderbarButton
        type={loanDrilldownSidebarTypes.FORMSPY}
        tooltip="View Loan Data"
        label="Data Preview"
      >
        <CodeOutlined className="thin-8" />
      </CollapsedSiderbarButton>
    </CollapsedContentWrapper>
  );
};

const CollapsedSiderbarButton = ({ type, children, tooltip, label }) => {
  const isActive = useSelector((state) => state.sidebar?.type === type);
  const dispatch = useDispatch();
  const loanDrilldown = useSelector((state) => state.loanDrilldown);
  const theme = useTheme();
  const navigate = useNavigate();

  const handleOnClick = () => {
    if (type === loanDrilldownSidebarTypes.UW_STIPS_AND_CONDITIONS) {
      navigate(`/loan/${loanDrilldown?.loanNumber}/conditions`);

      return;
    }
    dispatch(sidebarTypeToggle(type));
  };

  return (
    <Tooltip title={tooltip} placement={"left"} disableInteractive>
      <Button
        startIcon={children}
        onClick={handleOnClick}
        size="large"
        // color={isActive ? "primary" : "initial"}
        variant="text"
        color={isActive ? "primary" : "inherit"}
        // color={isActive ? "primary" : "inherit"}
        sx={{
          borderRadius: "0px",
          width: "64px",
          // width: "52px",
          px: 0.2,
          py: 2.5,
          fontSize: "8px",
          minWidth: "30px",
          // color: "inherit",
          // color: grey[800],
          lineHeight: 1.3,
          flexDirection: "column",
          // fontWeight: 400,
          fontWeight: isActive ? 600 : 400,
          background: isActive ? blue[50] : "transparent",
          // height: "72px",
          borderLeft: isActive
            ? `3px solid ${theme.palette.primary.main}`
            : "none",
          ".MuiButton-icon": {
            m: 0,
            // fill: isActive ? blue[50] : "inherit",
          },
          "&:hover": {
            background: blue[50],
          },
        }}
      >
        {label}
      </Button>
    </Tooltip>
  );
};

CollapsedContentWrapper.propTypes = {
  children: PropTypes.node,
};

CollapsedSiderbarButton.propTypes = {
  type: PropTypes.string,
  children: PropTypes.node,
  tooltip: PropTypes.string,
};

export default LoanDrilldownScreenSidebar;
